import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@vb/ui";
import { Seo, faqSchema } from "../../lib/seo.js";
import { Section } from "../../components/Section.js";

const ENGINE_SNIPPET = `import { hmac } from "@noble/hashes/hmac";
import { sha256 } from "@noble/hashes/sha2";
import { bytesToHex, hexToBytes, utf8ToBytes } from "@noble/hashes/utils";

function hmacHex(keyHex: string, message: string): string {
  return bytesToHex(hmac(sha256, hexToBytes(keyHex), utf8ToBytes(message)));
}

function hmacToCrashMultiplier(hmacHexStr: string): number {
  const first13 = hmacHexStr.slice(0, 13);
  const h = BigInt("0x" + first13);
  const e = 1n << 52n;
  const crash = Number((99n * e) / (h + 1n)) / 100;
  return Math.max(1.0, crash);
}

export function rollBust(serverSeed: string, clientSeed: string, nonce: number): number {
  const h = hmacHex(serverSeed, \`\${clientSeed}:\${nonce}:bust\`);
  return hmacToCrashMultiplier(h);
}

export function verify(
  serverSeed: string,
  clientSeedAndNonce: { clientSeed: string; nonce: number },
  claimedHash: string,
  claimedBust: number,
): { hashOk: boolean; bustOk: boolean } {
  const recomputedHash = bytesToHex(sha256(hexToBytes(serverSeed)));
  const recomputedBust = rollBust(serverSeed, clientSeedAndNonce.clientSeed, clientSeedAndNonce.nonce);
  return {
    hashOk: recomputedHash === claimedHash,
    bustOk: recomputedBust === claimedBust,
  };
}`;

const FAQ_ITEMS = [
  {
    q: "What does 'provably fair' actually prove?",
    a: "It proves that the operator committed to a specific server seed before the round started, and that the bust point is a deterministic function of that seed plus the player-chosen client seed and nonce. It does NOT prove anything about the operator's wallet, settlement, or RNG entropy quality — those are separate trust questions.",
  },
  {
    q: "Why do operators reveal the server seed only after the round?",
    a: "If the seed leaks before the round, the player can compute the exact bust multiplier and cash out one tick before. The pre-round commitment is the SHA-256 hash of the seed; the seed itself is held until resolve. Hash-then-reveal is the smallest cryptographic primitive that gives both sides what they need.",
  },
  {
    q: "Can the operator pre-mine bad seeds?",
    a: "Yes — any operator with control of the seed-generation process can roll thousands of seeds, pick ones with bad busts, and commit those. The defense is that the player chooses the client seed, and the bust depends on HMAC(server, 'client:nonce:bust'). Even a maliciously chosen server seed produces an unpredictable result once the player rotates their client seed.",
  },
  {
    q: "Is provably-fair the same as a regulator audit?",
    a: "No. Provably-fair gives you per-round verifiability with no third party. A regulator audit (GLI-19, iTechLabs) gives you a one-time independent statement that the math, RNG entropy, and code paths match what the operator says they do. Both have value — provably-fair is continuous and free; the audit is a snapshot and expensive.",
  },
  {
    q: "Where can I find Vault Breaker's verifier source?",
    a: "It's the @vb/engine package — MIT-licensed, vendored into our own apps, and re-derivable from the published seeds endpoint at /rgs/round/:id/seeds. The verifier is a 30-line function; you can paste it into a browser console and verify a round without ever calling our servers.",
  },
];

export function HowProvablyFairWorks() {
  return (
    <>
      <Seo
        title="How provably-fair crash games actually work · Vault Breaker Studios"
        description="Threat model, commit-reveal, HMAC-SHA256 derivation, the inverse-uniform crash CDF, and a paste-in browser verifier — the technical reality behind provably-fair claims."
        path="/blog/how-provably-fair-works"
        jsonLd={faqSchema(FAQ_ITEMS)}
      />

      <Section style={{ paddingBottom: 24 }}>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          ENGINEERING · 9 MIN READ
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(36px, 5vw, 56px)",
            lineHeight: 1.05,
            margin: 0,
            color: "var(--fg-1)",
            letterSpacing: "0.01em",
          }}
        >
          How provably-fair crash games actually work — and how to verify one in your browser.
        </h1>
        <p
          style={{
            fontSize: 16,
            color: "var(--fg-3)",
            marginTop: 16,
            fontFamily: "var(--font-mono)",
          }}
        >
          2026-05-05
        </p>
      </Section>

      <Section style={{ paddingTop: 0, maxWidth: 760 }}>
        <Prose>
          <p>
            Most crypto crash games claim to be "provably fair." Most of those claims survive about two
            minutes of scrutiny. This post is the technical version: what the threat model actually is,
            what the cryptographic scheme actually does, and a snippet you can paste into a browser
            console to verify a Vault Breaker round without trusting our backend, our frontend, or our
            audit page.
          </p>

          <H2>(a) The threat model — what could the operator lie about?</H2>
          <p>
            A crash game has one server-controlled output: the bust multiplier. If the operator can pick
            it after seeing player bets, they can extract more than the advertised house edge — set the
            bust low when whales are in, high when only bots are watching. There is no honest reason for
            the operator to know the bust before the round starts and refuse to commit to it publicly.
          </p>
          <p>
            Provably-fair is the cryptographic discipline of forcing the operator to commit to the bust
            output before any player bets, and then revealing enough information after the round that
            anyone — player, regulator, hostile journalist — can recompute it independently and confirm
            no swap happened.
          </p>

          <H2>(b) Commit-reveal — SHA-256 of the server seed</H2>
          <p>
            At session launch, the server picks a 32-byte random seed (we use{" "}
            <code className="mono" style={{ color: "var(--cyan)" }}>
              randomBytes(32)
            </code>{" "}
            from the same primitive Node's crypto module uses) and ships its SHA-256 hash to the client
            in the launch response. The raw seed never leaves the server until the session closes.
          </p>
          <p>
            Why hash first? Because the bust is a deterministic function of the seed. If the player
            could see the seed, they'd compute the bust before betting and cash out one tick before
            it crashed. Hashing commits the operator to that specific seed without leaking the bust.
          </p>
          <p>
            The cryptographic guarantee: SHA-256 is collision-resistant. The operator cannot find a
            second seed that hashes to the same value, so they cannot swap the seed mid-round to change
            the bust output.
          </p>

          <H2>(c) Per-round derivation — HMAC-SHA256(serverSeed, `clientSeed:nonce:bust`)</H2>
          <p>
            One server seed covers many rounds. Each round inside that session bumps a nonce (1, 2, 3,
            …). The bust for round N is computed as:
          </p>
          <pre style={preStyle}>
            <code>{`hmac = HMAC-SHA256(serverSeed, \`\${clientSeed}:\${nonce}:bust\`)`}</code>
          </pre>
          <p>
            The player chooses the client seed before the session starts — typically a UUID from their
            wallet. This matters: even if a malicious operator tried to pre-mine a server seed with bad
            busts, they'd need to do it for every possible client seed a player might pick. With a
            128-bit client seed they'd need <code className="mono">2^128</code> precomputations to game
            a single player. That's not a trade-off; that's infeasible.
          </p>

          <H2>(d) The CDF — turning a hash into a multiplier with a 1% house edge</H2>
          <p>
            HMAC-SHA256 gives you a uniformly distributed 256-bit output. We take the first 13 hex
            characters (52 bits, big enough for two-decimal precision, small enough to fit in a JS
            number) and treat it as a uniform integer{" "}
            <code className="mono">h ∈ [0, 2^52)</code>. The crash multiplier is:
          </p>
          <pre style={preStyle}>
            <code>{`E = 2^52
crash = floor((99 · E) / (h + 1)) / 100
crash = max(1.00, crash)   // floor at 1.00`}</code>
          </pre>
          <p>
            This is the Primedice / Stake CDF. The math: under cashout-at-c, the survival probability
            is{" "}
            <code className="mono">P(crash ≥ c) = 99 / (100·c)</code>, so the expected return on a bet
            of 1 unit cashed at c is{" "}
            <code className="mono">c · P(crash ≥ c) = 0.99</code> — invariant across every flat
            cashout strategy.
          </p>
          <p>
            The floor-to-1.00 region produces ~2% instabusts. That's not a policy choice — it's the
            shape of the distribution. Crash players who don't know this will assume the operator is
            "rigging" instabusts; the math just says the bottom 2% of the uniform distribution maps to
            "round ends instantly."
          </p>

          <H2>(e) Why house edge is invariant to player strategy</H2>
          <p>
            A common question from operators evaluating crash: "what if a player only cashes out at 1.01x
            — do they print money?" No. The CDF is constructed so that the expected return is 0.99
            regardless of c. Cash out at 1.01x and you win 99% of the time, but you only win 0.01 units;
            cash out at 100x and you win 0.99% of the time and pocket 99 units. Both strategies bleed
            1% per round in expectation.
          </p>
          <p>
            The only way to beat the house edge is to be the house. Variance (the wild swings around
            that 99% RTP) is what makes the game fun; the house edge is what makes it a business.
          </p>

          <H2>(f) In-browser verification — paste this into devtools</H2>
          <p>
            Below is the actual provably-fair function shipped in{" "}
            <code className="mono" style={{ color: "var(--cyan)" }}>@vb/engine</code>. It takes the
            three values returned from{" "}
            <code className="mono" style={{ color: "var(--cyan)" }}>
              GET /rgs/round/:id/seeds
            </code>{" "}
            (serverSeed, clientSeed, nonce) plus the original committed{" "}
            <code className="mono">serverSeedHash</code> and the bust multiplier you saw on screen. If
            both checks pass, you got the round you were promised — no Vault Breaker server involved
            in the verification.
          </p>
          <Card style={{ padding: 0, overflow: "hidden", marginTop: 12 }}>
            <pre style={{ ...preStyle, margin: 0, padding: "20px 22px", whiteSpace: "pre" }}>
              <code>{ENGINE_SNIPPET}</code>
            </pre>
          </Card>
          <p>
            Drop this in a TypeScript repl, install{" "}
            <code className="mono">@noble/hashes</code>, and you've reproduced our crash output without
            running a single line of our infrastructure. That's the bar. Anything less is a marketing
            page with a "fair" sticker on it.
          </p>

          <H2>(g) What an audit (GLI-19) adds on top</H2>
          <p>
            Provably-fair gives you per-round verifiability for free, forever. It does not give you:
          </p>
          <ul>
            <li>
              Independent verification that the deployed code path matches the open-source reference.
              An auditor reviews the production binary and signs that they match.
            </li>
            <li>
              Statistical sign-off that the RNG entropy source is unbiased over millions of rounds.
              Auditors run NIST SP 800-22 and Diehard suites; you don't want to do that yourself.
            </li>
            <li>
              A regulator-acceptable certificate. Curaçao, MGA, and most B2B-friendly regulators
              require GLI-19 or iTechLabs paperwork; cryptographic verifiability isn't enough on the
              compliance side.
            </li>
          </ul>
          <p>
            We commit to commencing GLI-19 (or iTechLabs equivalent) within 60 days of our first
            signed pilot. The provably-fair scheme is what we ship today; the audit is what makes the
            scheme legible to auditors and regulators tomorrow. They are complements, not substitutes.
          </p>

          <H2>The honest bar</H2>
          <p>
            If you build a casino frontend, vendor{" "}
            <code className="mono" style={{ color: "var(--cyan)" }}>@vb/engine</code> and let your
            players verify rounds without trusting you. That's the only honest provably-fair claim.
            Everything else is asking the player to trust your audit page — which they can't verify
            either.
          </p>
        </Prose>
      </Section>

      <Section style={{ maxWidth: 760 }}>
        <Card style={{ padding: 22, display: "grid", gap: 10 }}>
          <h3
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: 18,
              color: "var(--fg-1)",
              textTransform: "uppercase",
              letterSpacing: "0.01em",
            }}
          >
            Try it on a live round
          </h3>
          <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.6 }}>
            Open a sandbox, play a round, fetch the seeds, and run the snippet. Fifteen minutes,
            zero commitment.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Link to="/sandbox" style={{ textDecoration: "none" }}>
              <Button variant="gold">OPEN A SANDBOX</Button>
            </Link>
            <Link to="/security" style={{ textDecoration: "none" }}>
              <Button>SECURITY POSTURE →</Button>
            </Link>
          </div>
        </Card>
      </Section>
    </>
  );
}

const preStyle = {
  margin: "12px 0",
  padding: "14px 16px",
  background: "var(--bg-2)",
  border: "1px solid var(--line)",
  borderRadius: 6,
  fontFamily: "var(--font-mono)",
  fontSize: 13,
  color: "var(--fg-2)",
  lineHeight: 1.6,
  whiteSpace: "pre-wrap" as const,
  overflowX: "auto" as const,
};

function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        marginTop: 36,
        marginBottom: 8,
        fontFamily: "var(--font-display)",
        fontSize: 22,
        color: "var(--fg-1)",
        letterSpacing: "0.01em",
      }}
    >
      {children}
    </h2>
  );
}

function Prose({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 15,
        color: "var(--fg-2)",
        lineHeight: 1.75,
      }}
    >
      {children}
    </div>
  );
}
